import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Layers, RefreshCw, Check, Clock, PackagePlus } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { rupee, daysLabel } from "@/lib/format";
import PaymentModal from "@/components/PaymentModal";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export default function Subscription() {
  const { sub, reloadSub } = useOutletContext();
  const [pkg, setPkg] = useState("");
  const [payment, setPayment] = useState(null);
  const [modal, setModal] = useState(false);

  if (!sub) return <div className="text-muted-foreground">Loading…</div>;

  const tiers = sub.tiers || [];
  const addonTiers = sub.addon_tiers || [];
  const hasSub = !!sub.subscription && sub.access_level !== "restricted";
  const slotsUsedUp = hasSub && (sub.slots?.available_slots ?? 1) <= 0;
  const curProducts = sub.subscription?.products || 0;
  const planTiers = hasSub ? tiers.filter((t) => t.products > curProducts) : tiers;
  const activeAddons = sub.slots?.addons || [];
  const selectedTier = planTiers.find((t) => t.id === pkg);
  const base = selectedTier ? selectedTier.price : 0;
  const gst = Math.round(base * 0.18);
  const total = base + gst;

  const openPay = (p) => { setPayment(p); setModal(true); };

  const purchase = async () => {
    if (!pkg) { toast.error("Select a subscription package"); return; }
    try {
      const { data } = await api.post("/subscription/purchase", { package_id: pkg });
      openPay(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const buyAddonPackage = async (id) => {
    try {
      const { data } = await api.post("/subscription/addon-package", { package_id: id });
      openPay(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const renew = async () => {
    try {
      const { data } = await api.post("/renewal/choice", { choice: "yes" });
      openPay(data.payment);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-heading text-2xl font-extrabold">Subscription & Slots</h1>
        <p className="text-muted-foreground text-sm font-medium">All prices are + GST. Every plan is valid for exactly 1 year from activation.</p>
      </div>

      {hasSub && (
        <div className="bg-slate-900 text-white rounded-2xl p-5" data-testid="current-plan-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-white/50 font-bold">Current Plan</div>
              <div className="font-heading text-2xl font-extrabold">{sub.subscription.name || sub.slots.package_label}</div>
              <div className="text-sm text-white/70 font-medium flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {daysLabel(sub.days_remaining)} · valid 1 year</div>
            </div>
            <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 font-bold capitalize">{sub.access_level.replace("_", " ")}</Badge>
          </div>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div><div className="text-white/50 text-xs font-semibold">Included</div><div className="font-bold">{sub.slots.included_slots}</div></div>
            <div><div className="text-white/50 text-xs font-semibold">Additional</div><div className="font-bold">{sub.slots.additional_slots}</div></div>
            <div><div className="text-white/50 text-xs font-semibold">Used / Total</div><div className="font-bold">{sub.slots.used_slots} / {sub.slots.total_slots}</div></div>
            <div><div className="text-white/50 text-xs font-semibold">Yearly Billing</div><div className="font-bold text-primary">{rupee(sub.slots.total_yearly_billing)}</div></div>
          </div>
        </div>
      )}

      {hasSub && sub.subscription.beginner && (
        <div className="bg-sky-50 border border-sky-300 rounded-2xl p-5" data-testid="beginner-no-renew-note">
          <h3 className="font-heading font-bold text-sky-900">Beginner package · one-time</h3>
          <p className="text-sm text-sky-800 font-medium">The Beginner package can't be renewed. When your slots run out, buy an add-on package below, or move up to one of the bigger packages.</p>
        </div>
      )}

      {hasSub && !sub.subscription.beginner && (
        <div className="bg-card border rounded-2xl p-5">
          <h3 className="font-heading font-bold flex items-center gap-2"><RefreshCw className="h-4 w-4 text-primary" /> Renew Now</h3>
          <p className="text-xs text-muted-foreground mb-3">Extend your subscription by another 1 year.</p>
          <Button data-testid="renew-btn" onClick={renew} variant="outline" className="font-bold">Renew ({rupee(Math.round(sub.slots.total_yearly_billing * 1.18))} incl. GST)</Button>
        </div>
      )}

      {hasSub && activeAddons.length > 0 && <ActiveAddons addons={activeAddons} />}

      {(!hasSub || planTiers.length > 0) && (
        <div className="bg-card border rounded-2xl p-6" data-testid={hasSub ? "upgrade-section" : "choose-plan-section"}>
          <h2 className="font-heading text-lg font-bold flex items-center gap-2"><Layers className="h-5 w-5 text-primary" /> {hasSub ? "Upgrade to a Bigger Package" : "Choose Your Yearly Package"}</h2>
          <p className="text-sm text-muted-foreground font-medium mb-4">{hasSub ? "Pay the full price of the new package. A fresh 1-year term starts on approval; your active add-ons stay." : "Pick a plan based on how many products you want to list. Valid 1 year. All prices + GST."}</p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {planTiers.map((t) => (
              <button key={t.id} data-testid={`plan-${t.id}`} onClick={() => setPkg(t.id)}
                className={`text-left border-2 rounded-xl p-4 transition-all ${pkg === t.id ? "border-primary ring-2 ring-primary/20" : "hover:border-primary/40"}`}>
                <div className="font-heading font-extrabold flex items-center justify-between text-primary uppercase text-sm tracking-wide">{t.name || `${t.products} Products`} {pkg === t.id && <Check className="h-4 w-4 text-primary" />}</div>
                <div className="font-heading text-2xl font-extrabold mt-1">{rupee(t.price)}</div>
                <div className="text-xs text-muted-foreground">+ GST · 1 year · {t.products} products</div>
                {t.beginner && <Badge className="mt-1 border bg-sky-100 text-sky-800 border-sky-300" data-testid={`plan-beginner-${t.id}`}>Beginner · one-time, not renewable</Badge>}
                {t.features && <div className="text-xs font-semibold mt-1">{t.features}</div>}
              </button>
            ))}
          </div>

          <div className="mt-5 bg-secondary rounded-xl p-4 space-y-1 text-sm font-medium">
            <Row l={selectedTier ? (selectedTier.name || `${selectedTier.products} Products plan`) : "Select a plan"} v={rupee(base)} />
            <Row l="Subtotal" v={rupee(base)} />
            <Row l="GST (18%)" v={rupee(gst)} />
            <div className="border-t pt-1 flex justify-between font-heading font-extrabold text-base"><span>Grand Total</span><span className="text-primary">{rupee(total)}</span></div>
          </div>

          <Button data-testid="purchase-btn" onClick={purchase} disabled={!selectedTier} className="w-full font-bold h-11 mt-4">{hasSub ? "Upgrade & Pay" : "Proceed to Payment"}</Button>
        </div>
      )}

      {hasSub && (
        <div className="bg-card border rounded-2xl p-6" data-testid="addon-packages-section">
          <h2 className="font-heading text-lg font-bold flex items-center gap-2"><PackagePlus className="h-5 w-5 text-primary" /> Additional Packages</h2>
          <p className="text-sm text-muted-foreground font-medium mb-4">{slotsUsedUp ? "You've used all your product slots. " : ""}Add-on packages stack on top of your plan and add more product slots. Each add-on is valid 1 year from approval. All prices + GST.</p>
          {addonTiers.length === 0 ? (
            <div className="text-sm text-muted-foreground font-semibold border rounded-xl p-4 text-center" data-testid="no-addon-packages">No additional packages available right now. Please contact support.</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {addonTiers.map((t) => (
                <div key={t.id} data-testid={`addon-plan-${t.id}`} className="text-left border-2 rounded-xl p-4 flex flex-col">
                  <div className="font-heading font-extrabold text-primary uppercase text-sm tracking-wide">{t.name || `${t.products} Products`}</div>
                  <div className="font-heading text-2xl font-extrabold mt-1">{rupee(t.price)}</div>
                  <div className="text-xs text-muted-foreground">+ GST · adds {t.products} listings · 1 year</div>
                  {t.features && <div className="text-xs font-semibold mt-1">{t.features}</div>}
                  <Button data-testid={`buy-addon-${t.id}`} onClick={() => buyAddonPackage(t.id)} className="w-full font-bold mt-3">Buy · {rupee(Math.round(t.price * 1.18))}</Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <PaymentModal payment={payment} open={modal} onClose={() => setModal(false)} onDone={reloadSub} />
    </div>
  );
}

function ActiveAddons({ addons }) {
  return (
    <div className="bg-card border rounded-2xl p-5" data-testid="active-addons-section">
      <h3 className="font-heading font-bold flex items-center gap-2 mb-3"><PackagePlus className="h-4 w-4 text-primary" /> Active Add-ons</h3>
      <div className="divide-y text-sm">
        {addons.map((a) => (
          <div key={a.id} data-testid={`active-addon-${a.id}`} className="flex justify-between py-2 font-medium">
            <span>{a.name || "Add-on"} · +{a.products} slots</span>
            <span className="text-muted-foreground">valid till {new Date(a.expiry).toLocaleDateString("en-IN")}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Row({ l, v }) {
  return <div className="flex justify-between"><span className="text-muted-foreground">{l}</span><span>{v}</span></div>;
}
